import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { ApiService } from '../../../../core/services/ApiService';
import { IAuditoriumConfig } from '../../../auditorium/core/interfaces/IAuditoriumConfig';
import { ISeat } from '../../../auditorium/core/interfaces/ISeat';
import { KioskStore } from '../../../kiosk/core/stores/KioskStore';
import { StatisticsService } from '../../../statistics/core/services/StatisticsService';
import { ISector } from '../interfaces/ISector';
import { IShow } from '../interfaces/IShow';
import { IShowPriceCategory } from '../interfaces/IShowPriceCategory';
import { IShowSearchResult } from '../interfaces/IShowSearchResult';
import { IShowToReleaseReservations } from '../interfaces/IShowToReleaseReservations';
import { ShowsStore } from '../stores/ShowsStore';
import { ShowsUtils } from '../utils/ShowsUtils';
@Injectable({
  providedIn: 'root'
})
export class ShowsService {

  private readonly _basePath = '/cinemas';
  private readonly _apiService = inject(ApiService);
  private readonly _showsStore = inject(ShowsStore);
  private readonly _statisticsService = inject(StatisticsService);
  private readonly _kioskStore = inject(KioskStore);

  public handleShowDeleted(): Observable<void> {
    this._showsStore.showMovedModalOpen$.next();
    return of(undefined);
  }

  public getProgram(begin: dayjs.Dayjs, end: dayjs.Dayjs = begin.add(1, 'month')): Observable<IResponse<IShow[]>> {
    return this._apiService.get(
      `${this._basePath}/program`,
      {
        begin: begin.format(environment.servicesDateFormat),
        end: end.format(environment.servicesDateFormat)
      }
    ).pipe(
      map((res: IResponse<IShow[]>) => {
        res.data = res.data.map(s => ShowsUtils.updateShowVisibilityFlags(s));

        return res;
      }),
      tap((res: IResponse<IShow[]>) => {
        this._showsStore.shows.update(shows => {
          const showsMap = new Map(shows.map(show => [show.id, show]));
          res.data.forEach(show => showsMap.set(show.id, show));

          return Array.from(showsMap.values());
        });
      }),
      switchMap(
        res => this._statisticsService.getShowsStatistics()
          .pipe(map(() => res))
      )
    );
  }

  public setDashboardTwoWeeksView(isTwoWeeksView: boolean): void {
    this._showsStore.isDashboardTwoWeeksView.set(isTwoWeeksView);
  }

  public setShowPageTwoWeeksView(isTwoWeeksView: boolean): void {
    this._showsStore.isShowPageTwoWeeksView.set(isTwoWeeksView);
  }

  public selectDashboardTwoWeeksAuditorium(auditoriumId: number | null): void {
    this._showsStore.dashboardTwoWeeksAuditoriumId.set(auditoriumId);
  }

  public selectAuditorium(auditoriumId: number): void {
    this._showsStore.isAllAuditoriumsSelected.set(false);
    this._showsStore.selectedAuditorium.set(auditoriumId);
  }

  public selectAllAuditoriums(): void {
    this._showsStore.isAllAuditoriumsSelected.set(true);
  }

  public getSectorsByShowId(showId: string): Observable<IResponse<ISector[]>> {
    return this._apiService.get(`${this._basePath}/show/${showId}/sectors`);
  }

  public getShowById(showId: string): Observable<IResponse<IShow>> {
    return this._apiService.get(`${this._basePath}/show/${showId}`)
      .pipe(
        tap(res => this._showsStore.selectedShow.set(res.data))
      );
  }

  public getSectorSeats(showId: string, sectorId:string): Observable<IResponse<ISeat[]>> {
    return this._apiService.get(`${this._basePath}/show/${showId}/sector/${sectorId}/seats`)
      .pipe(
        map((res: IResponse<ISeat[]>) => {
          res.data.forEach(x => x.seatId = x.id);

          return res;
        })
      );
  }

  public getSectorConfig(showId: string, sectorId:string): Observable<IResponse<IAuditoriumConfig>> {
    return this._apiService.get(`${this._basePath}/show/${showId}/sector/${sectorId}/config`);
  }

  public getShowPriceCategories(showId: number): Observable<IResponse<IShowPriceCategory[]>> {
    return this._apiService.get(`${this._basePath}/show/${showId}/price-category`)
      .pipe(
        tap(res => {
          res.data.forEach(x => {
            x.packages = x.packages
              .filter(
                p =>
                  this._kioskStore.kioskProductsAndPackages().package.find(p2 => p2.id === p.id)
              );
          });

          this._showsStore.showPriceCategories.set(res.data);
        })
      );
  }

  public getReleaseReservationsShows(): Observable<IResponse<IShowToReleaseReservations[]>> {
    return this._apiService.get(`${this._basePath}/reservation-release-status`);
  }

  public searchShows(
    dateFrom: dayjs.Dayjs,
    dateTo: dayjs.Dayjs,
    auditoriumIds: number[],
    movieSearchTerm: string): Observable<IResponse<IShowSearchResult[]>> {
    return this._apiService.get(
      `${this._basePath}/shows/search`, {
        dateFrom: dateFrom.format(environment.servicesDateFormat),
        dateTo: dateTo.format(environment.servicesDateFormat),
        auditoriumIds: auditoriumIds,
        movieSearchTerm: movieSearchTerm
      }
    );
  }

  public blockSeats(
    showId: string,
    seatIds: number[],
    operationId?: string
  ): Observable<IResponse<unknown>> {
    return this._apiService.post(`${this._basePath}/show/${showId}/seats/block`, {
      seatIds,
      operationId
    });
  }

  public unblockSeats(
    showId: string,
    seatIds: number[],
    operationId?: string
  ): Observable<IResponse<unknown>> {
    return this._apiService.post(`${this._basePath}/show/${showId}/seats/unblock`, {
      seatIds,
      operationId
    });
  }

  public blockAllFreeSeats(
    showId: string,
    operationId?: string
  ): Observable<IResponse<unknown>> {
    return this._apiService.post(`${this._basePath}/show/${showId}/seats/block-all-free`, {
      operationId
    });
  }

  public unblockAllBlockedSeats(
    showId: string,
    operationId?: string
  ): Observable<IResponse<unknown>> {
    return this._apiService.post(`${this._basePath}/show/${showId}/seats/unblock-all-blocked`, {
      operationId
    });
  }
}
